import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { Activity, ChevronRight, PencilLine, X } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { PropertyRow } from '@/db/types';
import {
  computeProfile,
  countProfileAttributes,
  getActiveProperty,
  getSubtypeCoverageSignal,
  profileAttributeChecks,
} from '@/lib/tasks';

/**
 * Home-profile completeness card on the My Home tab.
 *
 * IMPORTANT: this card and the Home tab's profile ring MUST show the
 * same number. Both ultimately call `computeProfile` with the same
 * `ProfileSignals` shape — the math lives in one place
 * (`lib/tasks.ts`), this is just the labeled per-field UI that helps
 * the user see WHICH gaps to close.
 *
 * Why we don't compute attribute-coverage in isolation any more:
 *   - Earlier versions of this card divided "attributes filled" by
 *     "attributes total" to produce its own percentage, which is why
 *     the user saw 94% here AND 60% on Home for the same property.
 *     The overall profile percentage is weighted (80pt attributes +
 *     20pt subtype coverage), so a pure attribute ratio ignores the
 *     subtype half and over- or under-reports.
 *   - Photo upload is intentionally not counted anywhere now — many
 *     users prefer the default home image and shouldn't be penalized
 *     for that.
 *   - Task completion is intentionally not counted either — profile %
 *     reflects home DATA, not engagement. Streaks/activity live
 *     elsewhere.
 *
 * The tap-through sheet still lists unfilled attribute fields by name
 * (so the user knows exactly what to fix), but the headline percentage
 * is the same one the Home ring shows.
 */
interface ProfileCheck {
  key: string;
  label: string;
  done: boolean;
  route: string;
}

export function HealthCard() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [property, setProperty] = useState<PropertyRow | null>(null);
  const [overallPct, setOverallPct] = useState(0);
  // Subtype-eligible count — systems whose catalog kind has subtype
  // options and the user hasn't picked one yet. THIS is what "Tune my
  // plan" actually walks through (one card per missing subtype), so
  // it's the right number to put on the button.
  const [systemsToTune, setSystemsToTune] = useState(0);
  // Broader count — systems where every depth field (subtype, model,
  // filter size, installed year, nickname) is blank. Used only in the
  // subtitle copy as an ambient signal ("you could enrich N systems"),
  // never on the button — see #156 / image bug-report: button said
  // 32 but the Home chip said 3, because the button was using THIS
  // count instead of subtypesToTune.
  const [systemsMissingDetail, setSystemsMissingDetail] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);

  const fetch = useCallback(async () => {
    const p = await getActiveProperty(db);
    if (!p) {
      setProperty(null);
      return;
    }
    // Compute the SAME profile percentage the Home ring shows, using
    // the same helpers. This is the single source of truth.
    const subtypeCov = await getSubtypeCoverageSignal(db, p.id);
    const attrs = countProfileAttributes(p);
    setProperty(p);
    setOverallPct(
      computeProfile(p, {
        attributesFilled: attrs.filled,
        attributesTotal: attrs.total,
        subtypeEligibleSystems: subtypeCov.subtypeEligibleSystems,
        subtypesSelected: subtypeCov.subtypesSelected,
        hasCompletedAnyTask: false, // no longer used by the formula
      })
    );
    setSystemsToTune(
      Math.max(0, subtypeCov.subtypeEligibleSystems - subtypeCov.subtypesSelected)
    );
    // For the "X systems still need detail" subhead — count systems
    // where ZERO of the 5 depth fields are filled. We pick zero (not
    // "less than 5") to avoid nagging users who've already filled
    // most fields on a system.
    const emptySystems = await db.getFirstAsync<{ n: number }>(
      `SELECT COUNT(*) AS n FROM system
       WHERE property_id = ? AND deleted_at IS NULL
         AND subtype IS NULL AND model IS NULL
         AND filter_size IS NULL AND installed_year IS NULL
         AND nickname IS NULL`,
      p.id
    );
    setSystemsMissingDetail(emptySystems?.n ?? 0);
  }, [db]);

  useFocusEffect(useCallback(() => {
    fetch();
  }, [fetch]));

  if (!property) return null;
  const attrChecks = profileAttributeChecks(property);
  const filledCount = attrChecks.filter((c) => c.filled).length;
  const totalCount = attrChecks.length;
  const attributesBlank = totalCount - filledCount;

  // Only hide the card at a true 100. Earlier we hid at >=95 to swallow
  // rounding, but users got stuck staring at the Home ring with no way
  // to see which last attribute was blank. Better to keep the card up
  // until they're actually done — the per-field blanks list (rendered
  // below) tells them exactly what's missing.
  if (overallPct >= 100) return null;

  // The card subtitle should tell the user WHY they're not at 100 and
  // what the smallest next step is. We pick the largest remaining
  // bucket (attributes blank vs systems blank) and lead with that.
  const tone = overallPct >= 70 ? 'green' : overallPct >= 35 ? 'honey' : 'clay';
  const ringColor =
    tone === 'green' ? Palette.forest : tone === 'honey' ? Palette.honeyDeep : Palette.clay;

  let body: string;
  if (attributesBlank > 0 && systemsMissingDetail > 0) {
    body = `${attributesBlank} home detail${attributesBlank === 1 ? '' : 's'} + ${systemsMissingDetail} system${systemsMissingDetail === 1 ? '' : 's'} need filling. Both nudge tasks toward your home.`;
  } else if (attributesBlank > 0) {
    body =
      attributesBlank === 1
        ? `Just 1 home detail still blank. Tap to fill it in.`
        : `${attributesBlank} home details still blank. Each one sharpens the plan.`;
  } else if (systemsMissingDetail > 0) {
    body = `${systemsMissingDetail} system${systemsMissingDetail === 1 ? '' : 's'} still need install year, model, or filter size to tune their tasks.`;
  } else {
    body = `A couple of subtype chips left to pick — tap to tune.`;
  }

  const blanks: ProfileCheck[] = attrChecks
    .filter((c) => !c.filled)
    .map((c) => ({
      key: String(c.field),
      label: c.label,
      done: false,
      // Deep-link the user straight to the right card in the Tune
      // walkthrough by passing the field name as a query param. The
      // walkthrough reads `?field=…` on mount and jumps to that card
      // instead of starting at card 1 — so "Add heating fuel" lands on
      // the heating fuel card, not the first random blank.
      route: `/profile-setup?field=${encodeURIComponent(String(c.field))}`,
    }));

  return (
    <>
      <Pressable
        onPress={() => setSheetOpen(true)}
        style={({ pressed }) => [styles.card, pressed && { opacity: 0.92 }]}>
        <Ring pct={overallPct} color={ringColor} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={styles.headRow}>
            <Activity size={13} color={Palette.inkSoft} strokeWidth={2} />
            <Text style={styles.head}>HOME PROFILE</Text>
          </View>
          <Text style={styles.score}>
            {filledCount} of {totalCount} home details · {overallPct}% complete
          </Text>
          <Text style={styles.sub}>{body}</Text>
        </View>
        <ChevronRight size={14} color={Palette.inkFaint} strokeWidth={1.7} />
      </Pressable>

      <BlanksSheet
        open={sheetOpen}
        blanks={blanks}
        systemsToTune={systemsToTune}
        systemsMissingDetail={systemsMissingDetail}
        onPick={(check) => {
          setSheetOpen(false);
          router.push(check.route as never);
        }}
        onTuneSystems={() => {
          setSheetOpen(false);
          router.push('/profile-setup' as never);
        }}
        onEditHome={() => {
          setSheetOpen(false);
          router.push('/property/edit' as never);
        }}
        onClose={() => setSheetOpen(false)}
      />
    </>
  );
}

function BlanksSheet({
  open,
  blanks,
  systemsToTune,
  systemsMissingDetail,
  onPick,
  onTuneSystems,
  onEditHome,
  onClose,
}: {
  open: boolean;
  blanks: ProfileCheck[];
  /** Exact number of cards the Tune walkthrough will show — drives the
   *  CTA label so it matches the Home tab chip. */
  systemsToTune: number;
  /** Broader ambient count for the subtitle copy. Not used on buttons. */
  systemsMissingDetail: number;
  onPick: (check: ProfileCheck) => void;
  onTuneSystems: () => void;
  onEditHome: () => void;
  onClose: () => void;
}) {
  const insets = useSafeAreaInsets();
  const anim = useSharedValue(0);
  if (open) anim.value = withTiming(1, { duration: 220, easing: Easing.out(Easing.cubic) });
  else anim.value = withTiming(0, { duration: 160 });

  const backdropStyle = useAnimatedStyle(() => ({ opacity: anim.value * 0.55 }));
  const sheetStyle = useAnimatedStyle(() => ({
    opacity: anim.value,
    transform: [{ translateY: (1 - anim.value) * 40 }],
  }));

  return (
    <Modal animationType="none" transparent visible={open} onRequestClose={onClose}>
      <Animated.View style={[sheetStyles.backdrop, backdropStyle]}>
        <Pressable style={{ flex: 1 }} onPress={onClose} />
      </Animated.View>
      <View pointerEvents="box-none" style={sheetStyles.wrap}>
        <Animated.View style={[sheetStyles.sheet, { paddingBottom: 24 + insets.bottom }, sheetStyle]}>
          <View style={sheetStyles.head}>
            <View style={{ flex: 1 }}>
              <Text style={sheetStyles.eyebrow}>STILL TO FILL</Text>
              <Text style={sheetStyles.title}>
                {blanks.length === 0
                  ? 'All home details filled in'
                  : `${blanks.length} home detail${blanks.length === 1 ? '' : 's'} blank`}
              </Text>
              {systemsMissingDetail > 0 ? (
                <Text style={sheetStyles.subtitle}>
                  Plus {systemsMissingDetail} system{systemsMissingDetail === 1 ? '' : 's'} on this page need install year, model, or filter size.
                </Text>
              ) : null}
            </View>
            <Pressable
              onPress={onClose}
              hitSlop={10}
              style={({ pressed }) => [sheetStyles.closeBtn, pressed && { opacity: 0.7 }]}>
              <X size={18} color={Palette.ink} strokeWidth={2} />
            </Pressable>
          </View>
          <ScrollView style={{ maxHeight: 380 }} contentContainerStyle={{ paddingVertical: 4 }}>
            {blanks.map((c, i) => (
              <Pressable
                key={c.key}
                onPress={() => onPick(c)}
                style={({ pressed }) => [
                  sheetStyles.row,
                  i < blanks.length - 1 && sheetStyles.rowBorder,
                  pressed && { backgroundColor: '#EEF3EE' },
                ]}>
                <View style={sheetStyles.iconBubble}>
                  <View style={sheetStyles.emptyDot} />
                </View>
                <Text style={sheetStyles.rowTitle}>{c.label}</Text>
                <ChevronRight size={14} color={Palette.inkFaint} strokeWidth={1.7} />
              </Pressable>
            ))}
          </ScrollView>
          {/*
            Surfaced underneath the per-attribute list: a one-tap CTA into
            the systems walkthrough. We only show this when there's
            actually a batch of system rows missing detail — no point
            offering "tune your systems" when there are none to tune.
          */}
          {systemsToTune > 0 ? (
            <Pressable
              onPress={onTuneSystems}
              style={({ pressed }) => [
                sheetStyles.tuneBtn,
                pressed && { transform: [{ scale: 0.98 }] },
              ]}
              accessibilityLabel="Tune my plan — walk through systems missing a subtype pick">
              <Text style={sheetStyles.tuneBtnText}>
                Tune my plan · {systemsToTune} system{systemsToTune === 1 ? '' : 's'}
              </Text>
              <ChevronRight size={16} color={Palette.cream} strokeWidth={2.2} />
            </Pressable>
          ) : null}

          {/* Secondary path: Edit my home — full-form access for users who
              prefer scrolling one long page instead of stepping through
              cards. Outline-style so the Tune button stays the primary. */}
          <Pressable
            onPress={onEditHome}
            style={({ pressed }) => [
              sheetStyles.editBtn,
              pressed && { transform: [{ scale: 0.98 }], opacity: 0.92 },
            ]}
            accessibilityLabel="Open the full Edit my home form">
            <PencilLine size={15} color={Palette.forest} strokeWidth={2} />
            <Text style={sheetStyles.editBtnText}>Open Edit my home</Text>
          </Pressable>
        </Animated.View>
      </View>
    </Modal>
  );
}

function Ring({ pct, color }: { pct: number; color: string }) {
  const SIZE = 44;
  return (
    <View style={[ringStyles.outer, { width: SIZE, height: SIZE, borderRadius: SIZE / 2 }]}>
      <View style={[ringStyles.bg, { backgroundColor: Palette.sand }]} />
      <View
        style={[
          ringStyles.fill,
          { backgroundColor: color, transform: [{ rotate: `${(pct / 100) * 360 - 90}deg` }] },
        ]}
      />
      <View style={[ringStyles.inner, { backgroundColor: Palette.card }]}>
        <Text style={[ringStyles.pct, { color }]}>{pct}%</Text>
      </View>
    </View>
  );
}

const ringStyles = StyleSheet.create({
  outer: { overflow: 'hidden', alignItems: 'center', justifyContent: 'center' },
  bg: { ...StyleSheet.absoluteFillObject, borderRadius: 22 },
  fill: { ...StyleSheet.absoluteFillObject, opacity: 0.85 },
  inner: {
    position: 'absolute',
    top: 4, left: 4, right: 4, bottom: 4,
    borderRadius: 999,
    alignItems: 'center', justifyContent: 'center',
  },
  pct: { fontFamily: 'HankenGrotesk_700Bold', fontSize: 12, letterSpacing: -0.2 },
});

const styles = StyleSheet.create({
  card: {
    marginTop: Spacing.three,
    marginHorizontal: 18,
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: 14,
  },
  headRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  head: {
    fontFamily: Fonts.sansBold, fontSize: 11, color: Palette.inkSoft,
    letterSpacing: 0.6,
  },
  score: {
    marginTop: 4, fontFamily: Fonts.serifBold, fontSize: 15, color: Palette.ink,
    letterSpacing: -0.2,
  },
  sub: {
    marginTop: 2, fontFamily: Fonts.sansMedium, fontSize: 12.5,
    color: Palette.inkSoft, lineHeight: 16,
  },
});

const sheetStyles = StyleSheet.create({
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(15, 24, 18, 1)' },
  wrap: { ...StyleSheet.absoluteFillObject, justifyContent: 'flex-end', alignItems: 'center' },
  sheet: {
    width: '100%', maxWidth: 440,
    backgroundColor: Palette.sand,
    borderTopLeftRadius: 28, borderTopRightRadius: 28,
    paddingHorizontal: 18, paddingTop: 18, paddingBottom: 24,
  },
  head: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 10 },
  eyebrow: { fontFamily: Fonts.sansBold, fontSize: 11, color: Palette.honeyDeep, letterSpacing: 0.7 },
  title: {
    marginTop: 2,
    fontFamily: Fonts.serifBold, fontSize: 17, color: Palette.ink, letterSpacing: -0.3,
  },
  subtitle: {
    marginTop: 4,
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    lineHeight: 17,
  },
  closeBtn: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 8, paddingVertical: 12,
    borderRadius: 12,
  },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: Palette.line },
  iconBubble: {
    width: 28, height: 28, borderRadius: 14,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  emptyDot: {
    width: 10, height: 10, borderRadius: 5,
    borderWidth: 1.5, borderColor: Palette.forest,
    borderStyle: 'dashed',
  },
  rowTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink, flex: 1 },
  // "Tune my plan" forest button — sits under the attribute list as a
  // path into the per-system walkthrough. Forest fill so it reads as the
  // primary action, not just another row.
  tuneBtn: {
    marginTop: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14, paddingHorizontal: 16,
    borderRadius: 14,
    backgroundColor: Palette.forest,
  },
  tuneBtnText: {
    fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.cream,
    letterSpacing: 0.2,
  },
  // Secondary "Edit my home" — outline-style under the forest Tune button
  // so users have a one-tap path to the full edit page when the card
  // walkthrough isn't what they want.
  editBtn: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12, paddingHorizontal: 16,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  editBtnText: {
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.forest,
    letterSpacing: 0.2,
  },
});
